'use client';

import { useGateValue } from '@statsig/react-bindings';
import type { ReactNode } from 'react';
import { useEffect } from 'react';

import isBrowser from '@/utils/isBrowser';

import OneTrustConsentProvider from './OneTrustConsentProvider';
import OuraConsentManager from './OuraConsentManager';

interface ConsentManagerProviderProps {
  readonly children: ReactNode;
  readonly locale: string;
}

/**
 * ConsentManagerProvider handles the feature-gated switchover between
 * OuraConsentManager (legacy) and OneTrustConsentProvider (new)
 *
 * Feature gate: 'onetrust_segment_consent_manager'
 * - false: Use OuraConsentManager (supports server and client analytics)
 * - true: Use OneTrustConsentProvider (client analytics only)
 */
const ConsentManagerProvider = ({
  children,
  locale,
}: ConsentManagerProviderProps) => {
  const oneTrustConsentManagerEnabled = useGateValue(
    'onetrust_segment_consent_manager',
  );

  // TODO: Remove after OneTrust CM rollout
  // Update the global analytics instance with the OneTrust gate status
  useEffect(() => {
    if (
      isBrowser() &&
      typeof oneTrustConsentManagerEnabled === 'boolean' &&
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      window?.ouraAnalytics
    ) {
      window.ouraAnalytics.setOneTrustGateEnabled(
        oneTrustConsentManagerEnabled,
      );
    }
  }, [oneTrustConsentManagerEnabled]);

  if (oneTrustConsentManagerEnabled) {
    // New OneTrust implementation - client analytics only
    return <OneTrustConsentProvider>{children}</OneTrustConsentProvider>;
  }

  // Legacy OuraConsentManager - supports server and client analytics
  return (
    <>
      {children}
      <OuraConsentManager locale={locale} />
    </>
  );
};

export default ConsentManagerProvider;
